import { TAG_LABELS_VI, type AchievementDef } from "@nano/content";
import {
  ACHIEVEMENTS_EN,
  ARCHETYPES_EN,
  CHAPTERS_EN,
  FAMILIES_EN,
  HOUSES_EN,
  ITEMS_EN,
  LAB_COLORS_EN,
  SHOP_TABS_EN,
  TAGS_EN,
  TUTORIALS_EN,
} from "./content.en.ts";
import { language } from "./lang.ts";

/**
 * Display text for game content in the player's language. Content objects
 * carry their Vietnamese source; English comes from `content.en.ts` by id
 * and falls back to the source when a translation is missing.
 */
const english = (text: string | undefined, source: string): string => (language() === "en" ? (text ?? source) : source);

export const houseName = (house: { readonly id: string; readonly displayName: string }) =>
  english(HOUSES_EN[house.id]?.name, house.displayName);

export const houseDescription = (house: { readonly id: string; readonly description: string }) =>
  english(HOUSES_EN[house.id]?.description, house.description);

export const itemName = (item: { readonly id: string; readonly name: string }) => english(ITEMS_EN[item.id], item.name);

export const shopTabLabel = (tab: { readonly id: string; readonly label: string }) => english(SHOP_TABS_EN[tab.id], tab.label);

export const archetypeName = (archetype: { readonly id: string; readonly name: string }) =>
  english(ARCHETYPES_EN[archetype.id], archetype.name);

export const familyName = (family: { readonly id: string; readonly name: string }) => english(FAMILIES_EN[family.id], family.name);

const ROMAN = ["I", "II", "III", "IV", "V", "VI"];

export function achievementName(def: AchievementDef): string {
  const family = ACHIEVEMENTS_EN[def.family];
  if (!family) return english(undefined, def.name);
  return english(def.tierCount > 1 ? `${family.name} ${ROMAN[def.tier - 1] ?? def.tier}` : family.name, def.name);
}

export const achievementDescription = (def: AchievementDef) =>
  english(ACHIEVEMENTS_EN[def.family]?.describe(def.target), def.description);

export const chapterName = (houseId: string, chapter: { readonly chapter: number; readonly name: string }) =>
  english(CHAPTERS_EN[houseId]?.[chapter.chapter - 1]?.name, chapter.name);

export const chapterFocus = (houseId: string, chapter: { readonly chapter: number; readonly focus: string }) =>
  english(CHAPTERS_EN[houseId]?.[chapter.chapter - 1]?.focus, chapter.focus);

export const tutorialText = (houseId: string, tutorialId: string, step: number, source: string) =>
  english(TUTORIALS_EN[houseId]?.[tutorialId]?.[step], source);

export const labColorName = (color: { readonly id: string; readonly name: string }) => english(LAB_COLORS_EN[color.id], color.name);

/** Sorting tray label, e.g. "Hồng và Sao" or "Pink and Star". */
export const tagLabels = (tags: readonly string[]) =>
  tags.map((tag) => english(TAGS_EN[tag], TAG_LABELS_VI[tag] ?? tag)).join(language() === "en" ? " and " : " và ");
